import store from '../..';
import { GET_CLAIM_DETAILS_METADATA } from '../../../../constants/apiRoutes';
import { ApiError } from '../../../../types/Content';
import { httpClient } from '../../../../utils/httpClient';
import Logger from '../../../../utils/logger';
import { ApiPayloadUsed } from '../../stateSegments/brandsInformationStateSegment';
import {
  claimMetadataStateSegment,
  FilterDetails,
} from '../../stateSegments/claimMetadataStateSegment';

export interface ClaimMetadataResponse {
  success: boolean;
  message?: string;
  errors?: string[];
  data?: {
    filterDetails: FilterDetails[];
  };
}

export const getClaimMetadata = async (
  apiPayload: Pick<
    ApiPayloadUsed,
    'brand' | 'country' | 'indication' | 'targetAudience' | 'topics'
  > & {
    includes?: (string | undefined)[];
  }
): Promise<ClaimMetadataResponse> => {
  const {
    brand: brandName,
    country: countryName,
    indication: indications,
    targetAudience: audiences,
    topics: primaryMessages,
    includes,
  } = apiPayload;

  try {
    const response = await httpClient.get<ClaimMetadataResponse>(
      GET_CLAIM_DETAILS_METADATA({
        countryName,
        brandName,
        indications,
        audiences,
        primaryMessages,
        includes,
      })
    );
    const { data, success } = response as ClaimMetadataResponse;

    if (success && data) {
      return {
        success: true,
        data,
      };
    }
    return {
      success: false,
    };
  } catch (error) {
    const postError = error as ApiError;
    Logger.error(new Error(postError.message));
    return {
      success: false,
    };
  }
};

export const fetchClaimMetadata = async (
  payload: Pick<
    ApiPayloadUsed,
    'brand' | 'country' | 'indication' | 'targetAudience' | 'topics'
  > & {
    includes?: (string | undefined)[];
  }
) => {
  store.set(claimMetadataStateSegment, prev => {
    return {
      ...prev,
      isLoading: true,
      errorMessage: '',
    };
  });

  const { success, data } = await getClaimMetadata(payload);

  if (success && data) {
    store.set(claimMetadataStateSegment, prev => {
      return {
        ...prev,
        data: {
          filterDetails: data.filterDetails ?? [],
        },
        isLoading: false,
      };
    });
  } else {
    store.set(claimMetadataStateSegment, prev => {
      return {
        ...prev,
        isLoading: false,
        errorMessage: 'There has been an error in fetching claim metadata',
      };
    });
  }

  return success;
};

export const updateClaimMetadata = (filterDetails: FilterDetails[]) => {
  store.set(claimMetadataStateSegment, prev => {
    return {
      ...prev,
      data: {
        filterDetails,
      },
    };
  });
};

export const clearClaimMetadata = () => {
  store.set(claimMetadataStateSegment, prev => {
    return {
      ...prev,
      data: {
        filterDetails: [],
      },
      isLoading: false,
      hasNoData: false,
      errorMessage: '',
    };
  });
};

export const setClaimMetadataLoading = (isLoading: boolean) => {
  store.set(claimMetadataStateSegment, prev => {
    return {
      ...prev,
      isLoading,
    };
  });
};

export const setClaimMetadataError = (errorMessage: string) => {
  store.set(claimMetadataStateSegment, prev => {
    return {
      ...prev,
      errorMessage,
      isLoading: false,
    };
  });
};
